import { gql, useMutation, useQuery } from '@apollo/client'
import cloneDeep from 'lodash/cloneDeep'
import set from 'lodash/set'

import {
  ArtistNotificationSettingsQuery,
  ArtistNotificationSettingsQueryVariables,
  ArtistSettingFragmentDoc,
  EmailNotificationSettingsQuery,
  EmailNotificationSettingsQueryVariables,
  UpdateArtistNotificationSettingsMutation,
  UpdateArtistNotificationSettingsMutationVariables,
  UpdateUserNotificationSettingsMutation,
  UpdateUserNotificationSettingsMutationVariables,
  UserSettingFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { userClaims } = useCurrentUser()

  const isArtist = !!userClaims?.orgs?.[config.organizationId]?.artist

  const { showError } = useMessage()
  const { data } = useQuery<
    EmailNotificationSettingsQuery,
    EmailNotificationSettingsQueryVariables
  >(QUERY)

  const { data: artistData } = useQuery<
    ArtistNotificationSettingsQuery,
    ArtistNotificationSettingsQueryVariables
  >(ARTIST_QUERY, { skip: !isArtist })

  const [updateUserNotificationSettings] = useMutation<
    UpdateUserNotificationSettingsMutation,
    UpdateUserNotificationSettingsMutationVariables
  >(UPDATE_USER_NOTIFICATION_SETTINGS)

  const [updateArtistNotificationSettings] = useMutation<
    UpdateArtistNotificationSettingsMutation,
    UpdateArtistNotificationSettingsMutationVariables
  >(UPDATE_ARTIST_NOTIFICATION_SETTINGS)

  const notifications = data?.user?.settings?.notifications
  const artistNotifications = artistData?.artist?.settings?.notifications

  return {
    state: {
      isArtist,
      newPost: !!notifications?.post?.newPost?.email,
      replyPost: !!notifications?.post?.replyPost?.email,
      newMission: !!notifications?.mission?.newMission?.email,
      replyCommentMission: !!notifications?.mission?.replyCommentMission?.email,
      requestTrade: !!notifications?.trade?.requestTrade?.email,
      commentTrade: !!notifications?.trade?.commentTrade?.email,
      agreedTrade: !!notifications?.trade?.agreedTrade?.email,
      declinedTrade: !!notifications?.trade?.declinedTrade?.email,
      startCampaign: !!notifications?.campaign?.startCampaign?.email,
      endCampaign: !!notifications?.campaign?.endCampaign?.email,
      startLiveStream: !!notifications?.liveStream?.startLiveStream?.email,
      artistReplyPost: !!artistNotifications?.post?.replyPost?.email,
      artistNewThread:
        !!artistNotifications?.artistThreadChannel?.newThread?.email,
      artistMembershipSubscribe:
        !!artistNotifications?.membership?.subscribe?.email,
      announcementOfficial: !!notifications?.announcement?.official?.email,
      commentThread: !!notifications?.threadChannel?.commentThread?.email,
      replyCommentThread:
        !!notifications?.threadChannel?.replyCommentThread?.email,
      gachaUpcomingExpiry: !!notifications?.gacha?.upcomingExpiry?.email,
    },
    actions: {
      async onToggle(value) {
        await updateUserNotificationSettings({
          variables: { updateUserNotificationSettingsInputs: value },
          optimisticResponse() {
            const updateUserNotificationSettings = {
              id: data?.user?.id ?? '',
              settings: {
                notifications: cloneDeep(notifications),
                __typename: 'UserSettings',
              },
              __typename: 'User',
            }
            const [type, subType] = value.type.split('_')
            set(
              updateUserNotificationSettings,
              `settings.notifications.${type}.${subType}.${value.channel}`,
              value.flag
            )
            return { updateUserNotificationSettings }
          },
        }).catch(showError)
      },
      async onArtistToggle(value) {
        await updateArtistNotificationSettings({
          variables: { inputs: value },
          optimisticResponse() {
            const updateArtistNotificationSettings = {
              id: artistData?.artist?.id ?? '',
              settings: {
                notifications: cloneDeep(artistNotifications),
                __typename: 'ArtistSettings',
              },
              __typename: 'Artist',
            }
            const [type, subType] = value.type.split('_')
            set(
              updateArtistNotificationSettings,
              `settings.notifications.${type}.${subType}.${value.channel}`,
              value.flag
            )
            return { updateArtistNotificationSettings }
          },
        }).catch(showError)
      },
    },
  }
})

const QUERY = gql`
  query EmailNotificationSettings {
    user {
      id
      ...UserSetting
    }
  }
  ${UserSettingFragmentDoc}
`

const ARTIST_QUERY = gql`
  query ArtistNotificationSettings {
    artist {
      id
      ...ArtistSetting
    }
  }
  ${ArtistSettingFragmentDoc}
`

const UPDATE_USER_NOTIFICATION_SETTINGS = gql`
  mutation UpdateUserNotificationSettings(
    $updateUserNotificationSettingsInputs: UserNotificationSettingsInput!
  ) {
    updateUserNotificationSettings(
      inputs: $updateUserNotificationSettingsInputs
    ) {
      ... on User {
        id
        ...UserSetting
      }
    }
  }
  ${UserSettingFragmentDoc}
`

const UPDATE_ARTIST_NOTIFICATION_SETTINGS = gql`
  mutation UpdateArtistNotificationSettings(
    $inputs: ArtistNotificationSettingsInput!
  ) {
    updateArtistNotificationSettings(inputs: $inputs) {
      ... on Artist {
        id
        ...ArtistSetting
      }
    }
  }
  ${ArtistSettingFragmentDoc}
`
